import { type FieldPath } from 'react-hook-form';
import { RefrigerationUnitSafetyThreshold } from '@/data/schemas';

export interface RefrigerationUnitFormValues {
  detectorsToxicCount?: number | null;
  detectorsExplosiveCount?: number | null;
  detectorsCeilingCount?: number | null;
  detectorsHumanCount?: number | null;
  detectorPositions?: string[];
  thresholds: Array<{
    id: string;
    thresholdType: RefrigerationUnitSafetyThreshold['thresholdType'];
    unit: RefrigerationUnitSafetyThreshold['unit'];
    value: number | null;
    actions: string[];
  }>;
  calibrationNotes?: string;
  ventilationType?: string;
  ventilationFlowRateM3h?: number | null;
  ventilationNotes?: string;
  ventilationNaturalHighM2?: number | null;
  ventilationNaturalLowM2?: number | null;
  ventilationNaturalNotes?: string;
  ventilationExtractUnits?: number | null;
  ventilationExtractLogic?: string;
  ventilationExtractMode?: string;
  ventilationExtractAtex: boolean;
  ventilationExtractFlowM3h?: number | null;
  ventilationExtractOperation?: string;
  scrubberPresent: boolean;
  scrubberNotes?: string;
  scrubberAirFlowM3h?: number | null;
  scrubberWaterFlowM3h?: number | null;
  scrubberWaterFlowSpec?: string;
  scrubberContactTimeSeconds?: number | null;
  scrubberWaterSupply?: string;
  scrubberWasteCollection?: string;
  scrubberAtex: boolean;
  abatementSystemPresent: boolean;
  abatementSystemControlMode?: string;
  abatementSystemTrim: boolean;
  abatementSystemLimitSwitches: boolean;
  abatementSystemType?: string;
  abatementSystemSupply?: string;
  abatementSystemDisposal?: string;
  abatementSystemWaterFlowM3h?: number | null;
  emergencyDialerPresent: boolean;
  emergencyGeneratorPresent: boolean;
  emergencyGuardServicePresent: boolean;
  emergencyNotes?: string;
  emergencyEeTripButtons: boolean;
  emergencyEeTripDetector: boolean;
  emergencyElettroserraturaPresent: boolean;
  emergencyLightingPresent: boolean;
  emergencyLightingAtex: boolean;
  sgancioAutomaticoPresent: boolean;
  sogliaSgancioAutomatico?: string;
  pulsanteSgancioPresent: boolean;
  powerPreferredLine: boolean;
  powerGenerator: boolean;
  powerUps: boolean;
  powerNotes?: string;
  valvesReliefPressures?: string;
  valvesReliefPeriodicity?: number | null;
  valvesReliefNotes?: string;
  valvesIsolationPresent: boolean;
  valvesIsolationDiverter: boolean;
  valvesIsolationNotes?: string;
  detectionMaintenanceName?: string;
  detectionMaintenanceNotes?: string;
  vsReliefCollectorsCount?: number | null;
  vsReliefSizing?: string;
  abatementTankPresent: boolean;
  abatementVolumeMc?: number | null;
  abatementMinWaterMc?: number | null;
  abatementMinFreeMc?: number | null;
  abatementLocation?: '' | 'interrata' | 'fuori_terra';
  abatementSignage?: '' | 'segnalata' | 'non_segnalata';
  abatementAccessibility?: '' | 'agevole' | 'difficoltosa';
  abatementMaterial?: '' | 'ca' | 'acciaio_zincato' | 'vetroresina' | 'polietilene';
  abatementDimensions?: string;
  abatementOrientation?: '' | 'orizzontale' | 'verticale';
  abatementLevelInstrumentationPresent: boolean;
  abatementLevelInstrumentationType?: string;
  abatementContinuousPhMonitoring: boolean;
  abatementEmergencyPreferredLine: boolean;
  abatementEmergencyGenerator: boolean;
  abatementEmergencyUps: boolean;
  vvfDocs?: '' | 'prevista' | 'non_prevista';
  vvfDocsLocation?: string;
}

export class RefrigerationUnitFormError extends Error {
  constructor(
    message: string,
    readonly field?: FieldPath<RefrigerationUnitFormValues>
  ) {
    super(message);
    this.name = 'RefrigerationUnitFormError';
  }
}

export const trim = (value?: string | null): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const toOptionalNumber = (value: number | null | undefined): number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (!Number.isFinite(value)) return undefined;
  return Number(value);
};

export const toNonNegativeInt = (value: number | null | undefined, fallback = 0): number => {
  if (value === undefined || value === null) return fallback;
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.round(Number(value)));
};

export const toOptionalInt = (value: number | null | undefined): number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (!Number.isFinite(value)) return undefined;
  return Math.max(0, Math.round(Number(value)));
};

export const parseNumberList = (raw?: string): number[] | undefined => {
  if (!raw) return undefined;
  const parts = raw
    .split(/[;,\s]+/)
    .map((item) => Number(item.trim()))
    .filter((value) => Number.isFinite(value) && value >= 0) as number[];
  return parts.length ? parts : undefined;
};

export const sanitizeEnumValue = <T extends string>(
  value: string | undefined | null,
  allowed: readonly T[]
): T | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  return (allowed as readonly string[]).includes(trimmed) ? (trimmed as T) : undefined;
};

export const LOCATION_VALUES = ['interrata', 'fuori_terra'] as const;
export const SIGNAGE_VALUES = ['segnalata', 'non_segnalata'] as const;
export const ACCESSIBILITY_VALUES = ['agevole', 'difficoltosa'] as const;
export const LEVEL_INSTRUMENTATION_VALUES = ['elettronico', 'meccanico', 'visivo'] as const;
export const MATERIAL_VALUES = ['ca', 'acciaio_zincato', 'vetroresina', 'polietilene'] as const;
export const ORIENTATION_VALUES = ['orizzontale', 'verticale'] as const;
export const VVF_DOCS_VALUES = ['prevista', 'non_prevista'] as const;
